/**
 * Module layout engine: maps contact type to module states.
 * 
 * Pure function with no I/O, shared identically by detail and edit views.
 * Encodes the Requirement 5.2 matrix exactly.
 * 
 * Validates: Requirements 5.1, 5.2, 5.6, 5.7, 6.2, 6.3
 */

import type { ContactType } from '@akfdev-com/shared';
import type { ModuleId, ModuleLayoutResult, ModuleState } from './types';

/**
 * The module state matrix from Requirement 5.2.
 * Maps each contact type to the state of each module.
 */
const MODULE_STATE_MATRIX: Record<string, Record<ModuleId, ModuleState>> = {
  resident: {
    contactInformation: 'active',
    billingFinancialSummary: 'active',
    invoices: 'active',
    bills: 'greyed',
    serviceCharges: 'active',
    defaultServiceCharges: 'active',
    notes: 'active',
    documents: 'active',
    history: 'active',
  },
  supplier: {
    contactInformation: 'active',
    billingFinancialSummary: 'active',
    invoices: 'greyed',
    bills: 'active',
    serviceCharges: 'hidden',
    defaultServiceCharges: 'greyed',
    notes: 'active',
    documents: 'active',
    history: 'active',
  },
  customer: {
    contactInformation: 'active',
    billingFinancialSummary: 'active',
    invoices: 'active',
    bills: 'greyed',
    serviceCharges: 'hidden',
    defaultServiceCharges: 'hidden',
    notes: 'active',
    documents: 'active',
    history: 'active',
  },
};

/**
 * Modules that are 'active' across all contact types (universal modules).
 * Used for the fallback case when type is null/unknown/out-of-set.
 */
const UNIVERSAL_ACTIVE_MODULES: ModuleId[] = [
  'contactInformation',
  'billingFinancialSummary',
  'notes',
  'documents',
  'history',
];

/**
 * All module identifiers (for completeness check).
 */
const ALL_MODULES: ModuleId[] = [
  'contactInformation',
  'billingFinancialSummary',
  'invoices',
  'bills',
  'serviceCharges',
  'defaultServiceCharges',
  'notes',
  'documents',
  'history',
];

/**
 * Compute the module layout for a given contact type.
 * 
 * For known types ('resident', 'supplier', 'customer'):
 *   Assigns each module the state from the matrix (Req 5.2).
 * 
 * For null/unknown/out-of-set types:
 *   Marks only universally-active modules 'active', every other module 'hidden',
 *   and sets unrecognisedType = true (Req 5.7).
 *   Never performs a partial render.
 * 
 * Guarantees: every module in the result has exactly one state (totality, Req 5.1).
 * 
 * @param type - Contact type or null/undefined
 * @returns ModuleLayoutResult with states and unrecognisedType flag
 */
export function computeModuleLayout(
  type: ContactType | null | undefined,
): ModuleLayoutResult {
  // Check if type is recognized
  const isRecognized =
    type !== null && type !== undefined && type in MODULE_STATE_MATRIX;

  if (isRecognized) {
    // Known type: use the matrix
    return {
      states: MODULE_STATE_MATRIX[type],
      unrecognisedType: false,
    };
  }

  // Unrecognized/null/unknown type: fallback (Req 5.7)
  const states: Record<ModuleId, ModuleState> = {} as Record<
    ModuleId,
    ModuleState
  >;
  for (const moduleId of ALL_MODULES) {
    states[moduleId] = UNIVERSAL_ACTIVE_MODULES.includes(moduleId)
      ? 'active'
      : 'hidden';
  }

  return {
    states,
    unrecognisedType: true,
  };
}
